import React, { useEffect, useRef } from 'react';
import { TrackingContext } from '../../context/TrackingContext';
import { initAmplitude } from '../../analytics/amplitude';
import { AmplitudeClient, Callback } from 'amplitude-js';
import { UserType } from '../../types/user.type';
import { TrackingEvent } from '../../types/tracking-events.enum';
import { useRouter } from 'next/router';
import { TrackedRoutesMap } from '../../types/tracked-routes.map';
import { RoutesEnum } from '../../types/routes.enum';
import { getUser } from '../../util/local-storage';
import { EventProperties } from '../../types/tracking-event-properties.type';
import _ from 'lodash';
import { getDefaultBooking } from '../../util/booking';

interface Props {
    children: React.ReactNode;
}

const TrackingProvider = ({ children }: Props) => {
    const router = useRouter();
    const route = useRef<string>();
    const previousUserID = useRef<string | null | undefined>();
    const amplitude = useRef<AmplitudeClient>();

    const isBrowser = () => typeof window !== 'undefined';
    if (isBrowser()) {
        amplitude.current = initAmplitude();
    }

    // Track the page views and update the userID if needed
    useEffect(() => {
        function updateUserId() {
            const userId = getUser()?.authId;
            if (
                amplitude.current &&
                userId &&
                previousUserID.current !== userId
            ) {
                previousUserID.current = userId;
                amplitude.current.setUserId(userId);
            }
        }

        if (isBrowser()) {
            if (!amplitude.current) {
                amplitude.current = initAmplitude();
            }
            const event = TrackedRoutesMap.get(router.pathname as RoutesEnum);
            if (
                router.pathname &&
                route.current !== router.pathname &&
                isBrowser() &&
                amplitude.current &&
                event
            ) {
                updateUserId();
                route.current = router.pathname;
                amplitude.current.logEvent(event, { path: router.pathname });
            }
        }
    }, [router.pathname]);

    const addPageEventProperty = (
        eventProperties?: EventProperties,
    ): EventProperties | undefined => {
        return route.current
            ? _.assign(eventProperties, { path: route.current })
            : eventProperties;
    };

    const anonymizeUser = (user: Partial<UserType> = {}): Partial<UserType> => {
        return {
            age: (user && user.age) || undefined,
            gender: (user && user.gender) || undefined,
        };
    };

    const setUserId = (userId: string | null | undefined): void => {
        if (amplitude.current) {
            previousUserID.current = userId;
            amplitude.current.setUserId(userId || null);
        }
    };

    const setUserProperties = (user?: Partial<UserType>): void => {
        if (amplitude.current) {
            amplitude.current.setUserProperties({
                ...anonymizeUser(user),
                bookingType: getDefaultBooking(user?.bookings)?.bookingType,
            });
        }
    };

    const sendEvent = (
        event: TrackingEvent,
        eventProperties?: EventProperties,
        callback?: Callback,
    ): void => {
        if (amplitude.current) {
            amplitude.current.logEvent(
                event,
                addPageEventProperty(eventProperties),
                callback,
            );
        }
    };

    const debouncedSendEvent = _.debounce(
        (event: TrackingEvent) => sendEvent(event),
        1000,
    );

    const clearUserProperties = (): void => {
        if (amplitude.current) {
            amplitude.current.clearUserProperties();
        }
    };

    const contextValue = {
        sendEvent,
        debouncedSendEvent,
        setUserProperties,
        setUserId,
        clearUserProperties,
    };

    return (
        <TrackingContext.Provider value={contextValue}>
            {children}
        </TrackingContext.Provider>
    );
};

export default TrackingProvider;
